import { inject } from '@angular/core';
import { signalStore, withMethods, withProps } from '@ngrx/signals';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  AccessLevel,
  DocumentAttribute,
  DocumentReferenceEntityType,
  PersonDocumentReference,
} from '@qtek/shared/models';
import { buildDocumentReferenceFormData } from '@qtek/shared/utils';
import { map, Observable } from 'rxjs';
import { DocumentScanGate } from '../../document-scan-gate.service';
import { UserStore } from '../../../user/user.store';
import { withDocumentReferenceMethods } from '../core/document-reference.store';
import { PersonDocumentReferenceService } from './person-document-reference.service';

export interface UploadPersonAvatarPayload {
  file: Blob;
  fileName: string;
  entityId: string;
  description?: string;
  accessLevel?: AccessLevel;
}

/**
 * Generic document-reference state/calls plus personal-avatar-specific
 * convenience on top, mirroring `CompanyDocumentReferenceStore`'s
 * logo pattern. `PERSONAL_IMAGE` is the new-system attribute closest to the
 * legacy numeric `OldDocumentAttribute.PERSONAL_AVATAR` (the new `DocumentAttribute`
 * enum has no dedicated "avatar" value).
 */
export const PersonDocumentReferenceStore = signalStore(
  { providedIn: 'root' },
  withDocumentReferenceMethods(PersonDocumentReferenceService),
  withProps(store => ({
    /** Newest cached `PERSONAL_IMAGE` reference, or null. */
    avatar: store.latestFor({ attribute: DocumentAttribute.PERSONAL_IMAGE }),
  })),
  withMethods(
    (
      store,
      scanGate = inject(DocumentScanGate),
      service = inject(PersonDocumentReferenceService),
      snackBar = inject(SnackBarService),
      userStore = inject(UserStore)
    ) => ({
      /**
       * `entityId` defaults to the signed-in user's own person id, so
       * call sites that don't own a person id (e.g. `loadAvatar()` with no
       * args) still scope the request instead of pulling every cached
       * `PERSONAL_IMAGE` across people.
       */
      loadAvatar(entityId?: string): void {
        store.load({
          attribute: DocumentAttribute.PERSONAL_IMAGE,
          entityId: entityId ?? userStore.currentPerson()?.personId,
        });
      },

      /** Commit gated on scan-settle, so no reader ever sees an unscanned avatar. */
      uploadAvatar(
        payload: UploadPersonAvatarPayload
      ): Observable<PersonDocumentReference | null> {
        const formData = buildDocumentReferenceFormData({
          file: payload.file,
          fileName: payload.fileName,
          entityType: DocumentReferenceEntityType.PERSON,
          entityId: payload.entityId,
          attribute: DocumentAttribute.PERSONAL_IMAGE,
          accessLevel: payload.accessLevel ?? AccessLevel.PUBLIC,
          description: payload.description ?? 'Person Avatar',
        });

        return store.upload(formData, reference =>
          scanGate
            .whenReferenceSettled(service, {
              referenceId: reference._id,
              attribute: DocumentAttribute.PERSONAL_IMAGE,
              entityId: payload.entityId,
              virusScanStatus: reference.document?.virusScanStatus,
            })
            .pipe(
              map(outcome => {
                if (outcome === 'ready') {
                  return true;
                }
                snackBar.showSnackBar({
                  type: 'ErrorMessage',
                  message: 'ERR_IMAGE_LOAD_FAILED',
                } as ErrorSnackBarData);
                return false;
              })
            )
        );
      },

      removeAvatar(id: string): Observable<void> {
        return store.remove(id);
      },
    })
  )
);
